import { Component, Fragment } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { endOfDate, startOfDate } from 'utils/date';
import { IUsers, RootState } from 'store/store';
import { withDataContext, IDataContext } from 'store/context/DataContext';
import Modal from 'components/Modal';
import Select from 'components/Select';
import { IOption } from 'components/Select/Select';
import RelatedResponsesContainer from './RelatedResponsesContainer';
import {
  RelatedResponsesSelectFilters,
  LoadingRelatedResponsesMessage,
} from './styles';
import { appDispatch } from 'store/store';
import { IAnalyticsQuery } from 'store/entity/IAnalyticsQuery';
import { IOpenItem } from 'store/entity/IOpenItem';
import {
  IGridAnswerView,
  IProviderSurveyReport,
  IQuestionsView,
  ISurveyItem,
  ISurveyResponseView,
  ISurveyViewServer,
  ISurveyViewWithData,
  TimeScale,
} from 'store/survey/surveyTypes';
import { IAnalyticsListQuery } from 'store/entity/IAnalyticsListQuery';
import { IQueryFilter } from 'store/entity/IQueryFilter';
import {
  requireSeveralUser,
  requireSeveralUserFn,
} from 'store/users/usersSlice';
import { getAnalyseList, getAnalyseListFn } from 'store/survey/surveySlice';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { removeDuplicateObjectsFromArray } from 'utils/happyFunctions';
import { IProviders, ITags, ITagView } from 'store/providers/providerTypes';
import i18n, { getCalendarLocal } from '../../../i18n';

interface IOwnProps {
  dataContext: IDataContext;
  isActive: boolean;
  onCloseModal: () => void;
  query: IAnalyticsQuery;
  reportItem: IOpenItem;
  selectedReport: number | null;
  selectedTagId?: number;
  survey?: ISurveyViewWithData;
  tags: ITags;
  surveyItem?: ISurveyItem;
  targetedItem: {
    itemId?: string | number;
    queryKey: string;
    itemId2?: number | string;
  };
  extraTagIds?: number[];
  isInDashboardView: boolean;
}

interface IDispatchToProps {
  getAnalyseList: (
    dataQuery: IAnalyticsListQuery,
    ownerToken?: string,
    reportToken?: string,
    surveyToken?: string
  ) => ReturnType<typeof getAnalyseListFn>;
  requireSeveralUser: (
    usersList: number[]
  ) => ReturnType<typeof requireSeveralUserFn>;
}

interface IStateToProps {
  users: IUsers['map'];
  thisUser: IThisUserState;
  tags: ITags;
  providers: IProviders;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

interface IComponentStates {
  loading: boolean;
  responses?: Array<ISurveyResponseView>;
  tagsOptions: IOption[];
  selectedTags: IOption[];
  choicesOptions: IOption[];
  selectedChoices: IOption[];
  relatedSurveys?: ISurveyViewServer[];
}

const getQuestionTags: (tags: ITagView[]) => IOption[] = (tags: ITagView[]) => {
  return tags.map((tag) => {
    return { value: tag.id, label: tag.text };
  });
};

class RelatedResponsesModal extends Component<
  IComponentProps,
  IComponentStates
> {
  private areChoiceValuesEqual = (
    selectedValue: string | number,
    answerValue: string | number | undefined
  ): boolean => {
    if (answerValue === undefined || answerValue === null) return false;
    return String(selectedValue) === String(answerValue);
  };

  private matchesSelectedChoice = (
    answer: ISurveyResponseView['surveyAnswerList'][number],
    selectedChoices: IOption[]
  ): boolean => {
    if (answer.type === 'grid') {
      const subAnswers = (answer as IGridAnswerView).subAnswers || [];
      return subAnswers.some((sub) =>
        selectedChoices.some((choice) =>
          this.areChoiceValuesEqual(choice.value as string | number, sub.value)
        )
      );
    }

    if (answer.type === 'multipleChoice') {
      const multiAnswer = answer as any;
      const selectedList: Array<string | number> = Array.isArray(
        multiAnswer.selected
      )
        ? multiAnswer.selected
        : [];
      const valuesList: Array<string | number> = Array.isArray(
        multiAnswer.values
      )
        ? multiAnswer.values
        : [];

      return selectedChoices.some((choice) => {
        const choiceValue = choice.value as string | number;
        return (
          this.areChoiceValuesEqual(choiceValue, multiAnswer.value) ||
          selectedList.some((selectedItem) =>
            this.areChoiceValuesEqual(choiceValue, selectedItem)
          ) ||
          valuesList.some((valueItem) =>
            this.areChoiceValuesEqual(choiceValue, valueItem)
          )
        );
      });
    }

    return false;
  };

  constructor(props: IComponentProps) {
    super(props);
    let propTagIds = props.selectedTagId ? [props.selectedTagId] : [];
    if (props.query.buckets[0].field === 'q_tags') {
      propTagIds.push(props.targetedItem.itemId as number);
    }
    props.query.filters.forEach((f) => {
      if (f.type === 'termFilter' && f.field === 'q_tags') {
        if (
          !props.targetedItem.itemId ||
          !(f.values as number[]).includes(props.targetedItem.itemId as number)
        )
          propTagIds = [...propTagIds, ...(f.values as number[])];
      }
    });
    const tag = propTagIds.map((propTagId: number) => {
      // const idx = Object.keys(props.tags.data).find(
      //   (t) => props.tags.data[t].id === propTagId
      // );
      // if (props.tags.data[propTagId]) {
      //   return props.tags.data[propTagId];
      // }
      return props.tags.data[propTagId];
    });
    const selectedTags = tag.map((tag) => {
      return { value: tag!.id, label: tag!.text! };
    });
    this.state = {
      loading: true,
      tagsOptions: [],
      selectedTags,
      choicesOptions: [],
      selectedChoices: [],
    };
  }

  componentDidMount(): void {
    const { reportItem, survey, selectedReport } = this.props;
    const filters = this.prepareFilters();
    const dataQuery: IAnalyticsListQuery = {
      filters,
    };
    let surveyToken = survey ? survey.data.surveyToken : undefined;
    let ownerToken;
    let reportToken;
    if (reportItem) {
      const {
        surveyReportLinks,
        ownerToken: ownerT,
        reportToken: reportT,
        reportIds,
      } = reportItem.item.data as IProviderSurveyReport;
      // it's a chart from a report item
      if (
        selectedReport &&
        (!reportIds || reportIds.indexOf(selectedReport!) === -1) &&
        surveyReportLinks
      ) {
        // if user opens a node of chart from a saved report
        const reportLink = surveyReportLinks.find(
          (link) => link.reportId === selectedReport
        );
        if (reportLink) {
          surveyToken = reportLink.surveyReportToken;
        }
      } else {
        ownerToken = ownerT;
        reportToken = reportT;
      }
    }

    this.props
      .getAnalyseList(dataQuery, surveyToken, ownerToken, reportToken)
      .then((data) => {
        const users = data.result.response.reduce(
          (usersList: number[], response: ISurveyResponseView) => {
            if (response.userId && usersList.indexOf(response.userId) < 0) {
              usersList.push(response.userId);
            }
            return usersList;
          },
          []
        );
        this.props.requireSeveralUser(users).then(() => {
          const tagsOptions: IOption[] = [];
          const choicesOptions: IOption[] = [];
          data.result.relatedSurveys.forEach((survey: ISurveyViewServer) => {
            (survey.id === this.props.survey?.data.id
              ? (this.props.survey.data.versions[
                  this.props.survey.viewingVersion!
                ]?.questionList ?? survey.surveyQuestionList)
              : survey.surveyQuestionList
            ).forEach((question: IQuestionsView) => {
              if (question.deleted || question.distinctive) return;
              if (question.type === 'grid') {
                const gridQuestionTags = question?.tags;
                question.choiceList.forEach((choice) => {
                  if (
                    !choicesOptions.find((item) => item.value === choice.value)
                  ) {
                    choicesOptions.push({
                      value: choice.value,
                      label: choice.text,
                    });
                  }
                  if (gridQuestionTags && gridQuestionTags.length > 0) {
                    tagsOptions.push(...getQuestionTags(gridQuestionTags));
                  }
                });
              } else if (question.type === 'multipleChoice') {
                const multipleChoiceQuestionTags = question.tags;
                if (question.useTags) {
                  if (question.parentTagChoices) {
                    question.parentTagChoices.forEach((parentTagId) => {
                      Object.values(this.props.tags.data)
                        .filter(
                          (tag: ITagView) =>
                            tag.parentId === parentTagId &&
                            !question.excludedTagChoices?.includes(
                              Number(tag.id)
                            ) &&
                            !tag.deleted
                        )
                        .forEach((tag: ITagView) => {
                          if (
                            !choicesOptions.find(
                              (item) => item.value === tag.id
                            )
                          ) {
                            choicesOptions.push({
                              value: tag.id,
                              label: tag.text,
                            });
                          }
                        });
                    });
                  }
                  if (question.tagChoices) {
                    question.tagChoices.forEach((tagId) => {
                      if (
                        !choicesOptions.find((item) => item.value === tagId)
                      ) {
                        choicesOptions.push({
                          value: tagId,
                          label: this.props.tags.data[tagId].text,
                        });
                      }
                    });
                  }
                } else {
                  question.choiceList.forEach((choice) => {
                    if (
                      !choicesOptions.find(
                        (item) => item.value === choice.value
                      )
                    ) {
                      choicesOptions.push({
                        value: choice.value,
                        label: choice.text,
                      });
                    }
                  });
                }
                if (
                  multipleChoiceQuestionTags &&
                  multipleChoiceQuestionTags.length > 0
                ) {
                  tagsOptions.push(
                    ...getQuestionTags(multipleChoiceQuestionTags)
                  );
                }
              }
            });
          });

          const uniqueTagsOptions = removeDuplicateObjectsFromArray(
            tagsOptions,
            'label'
          );
          const uniqueChoicesOptions = removeDuplicateObjectsFromArray(
            choicesOptions,
            'value'
          );

          this.setState({
            loading: false,
            responses: data.result.response,
            tagsOptions: uniqueTagsOptions,
            choicesOptions: uniqueChoicesOptions,
            relatedSurveys: data.result.relatedSurveys,
          });
        });
      });
  }

  makeStartDate = (
    timeScale: TimeScale,
    effectiveDate: number,
    type: 'start' | 'end'
  ): number => {
    if (type === 'start') {
      return startOfDate(new Date(effectiveDate), timeScale, true);
    }
    return endOfDate(new Date(effectiveDate), timeScale, true);
  };

  makeTimeScale = (gap: string): TimeScale => {
    switch (gap) {
      case '+7DAYS':
        return 'week';
      case '+31DAYS':
        return 'month';
      case '+93DAYS':
        return 'season';
      case '+186DAYS':
        return 'twoSeason';
      case '+366DAYS':
        return 'year';
      default:
        return 'day';
    }
  };

  prepareFilters = (): IQueryFilter[] => {
    const { targetedItem, query } = this.props;
    let filters = cloneDeep(query.filters);
    const firstBucket = query.buckets[0];
    if (query.buckets.length > 0) {
      if (firstBucket.type === 'dateRangeBucket') {
        // change start and end dates
        filters = filters.map((filterItem) => {
          if (
            filterItem.field === 'effectiveDate' &&
            targetedItem.itemId !== '__total'
          ) {
            const timeScale = this.makeTimeScale(firstBucket.gap as string);
            filterItem.start = this.makeStartDate(
              timeScale,
              targetedItem.itemId as number,
              'start'
            );
            filterItem.end = this.makeStartDate(
              timeScale,
              targetedItem.itemId as number,
              'end'
            );
            return filterItem;
          }
          return filterItem;
        });
      }
    }
    if (targetedItem.itemId && targetedItem.itemId !== '__total') {
      const field = firstBucket.field;
      let values: string[] | number[] = [targetedItem.itemId as number];
      if (field === 'allAnswers') {
        values = [`${firstBucket.prefix}$$${targetedItem.itemId}`];
      }
      if (field === 'effectiveJMonth') {
        const start = values[0] as number;
        for (let i = start + 1; i < start + Number(firstBucket.gap); i++) {
          (values as number[]).push(i);
        }
      }
      const newFilter: IQueryFilter = {
        type: 'termFilter',
        values,
        field,
      };
      filters.push(newFilter);
    }
    if (
      targetedItem.itemId2 &&
      firstBucket.subBuckets?.length &&
      targetedItem.itemId2 !== '__total'
    ) {
      const field = firstBucket.subBuckets[0].field;
      let values: string[] | number[] = [targetedItem.itemId2 as number];
      if (field === 'allAnswers') {
        values = [
          `${firstBucket.subBuckets[0].prefix}$$${targetedItem.itemId2}`,
        ];
      }
      const newFilter2: IQueryFilter = {
        type: 'termFilter',
        values,
        field,
      };
      filters.push(newFilter2);
    }
    return filters;
  };

  handleChangeChoices = (selectedChoices: IOption[]) => {
    this.setState({ selectedChoices });
  };

  handleChangeTags = (selectedTags: IOption[]) => {
    this.setState({ selectedTags });
  };

  render() {
    let responses = this.state.responses;
    if (this.state.selectedChoices.length > 0 && this.state.responses) {
      const filtered = this.state.responses.filter((response) => {
        return response.surveyAnswerList.find((answer) => {
          return this.matchesSelectedChoice(answer, this.state.selectedChoices);
        });
      });
      responses = filtered;
    }

    const targetedTagId = Number(this.props.targetedItem?.itemId);
    const title = `${i18n.t('relatedResponses', { ns: 'survey' })} ${
      this.props?.tags?.data?.[targetedTagId]?.text || ''
    }`;

    return (
      <Modal
        width="95%"
        title={title}
        onClose={this.props.onCloseModal}
        isActive={this.props.isActive}
      >
        {this.state.loading ? (
          <LoadingRelatedResponsesMessage>
            {i18n.t('loadingRelatedResponseList', { ns: 'survey' }) as string}
          </LoadingRelatedResponsesMessage>
        ) : (
          <Fragment>
            {!this.props.isInDashboardView && (
              <RelatedResponsesSelectFilters>
                <Select
                  label={
                    i18n.t('filterWithChoices', { ns: 'survey' }) as string
                  }
                  values={this.state.selectedChoices}
                  options={this.state.choicesOptions}
                  multiChoice={true}
                  placeholder={i18n.t('addChoices', { ns: 'survey' }) as string}
                  onChangeValues={this.handleChangeChoices}
                />
                <Select
                  label={
                    i18n.t('filterWithQuestionsTags', {
                      ns: 'survey',
                    }) as string
                  }
                  values={this.state.selectedTags}
                  options={this.state.tagsOptions}
                  multiChoice={true}
                  placeholder={
                    i18n.t('addQuestionTags', { ns: 'survey' }) as string
                  }
                  onChangeValues={this.handleChangeTags}
                />
              </RelatedResponsesSelectFilters>
            )}
            <RelatedResponsesContainer
              survey={this.props.survey}
              surveyItem={this.props.surveyItem}
              title={title}
              tags={this.props.tags.data}
              users={this.props.users}
              surveys={this.state.relatedSurveys as any}
              thisUser={this.props.thisUser}
              responses={responses!}
              selectedTags={this.state.selectedTags.map(
                (tag) => tag.value as number
              )}
              selectedChoices={this.state.selectedChoices.map(
                (choice) => choice.value
              )}
              local={
                this.props.surveyItem?.providerId
                  ? this.props.providers.map?.[this.props.surveyItem.providerId]
                      ?.data?.surveyOwnerProperties.localId ||
                    getCalendarLocal()
                  : getCalendarLocal()
              }
              providerId={
                this.props.surveyItem?.providerId ||
                this.props.reportItem?.item?.data?.providerId
              }
              isInDashboardView={this.props.isInDashboardView}
            />
          </Fragment>
        )}
      </Modal>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  let providerId = (props.reportItem?.item?.data?.providerId ||
    props.surveyItem?.providerId) as number;
  if (props.dataContext.providerId) {
    providerId = Number(props.dataContext.providerId);
  }
  const users = state.users.map;
  const thisUser = state.thisUser;
  const tags = state.providers.tags[providerId]!;
  const providers = state.providers;

  return { users, thisUser, tags, providers };
};

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = (
  _,
  ownProps
) => ({
  getAnalyseList: (
    dataQuery: IAnalyticsListQuery,
    surveyToken?: string,
    ownerToken?: string,
    reportToken?: string
  ) => {
    if ((ownProps.extraTagIds?.length ?? 0) > 0) {
      if (!dataQuery.filters) dataQuery.filters = [];
      dataQuery.filters.push({
        field: 'tags',
        values: ownProps.extraTagIds,
        type: 'termFilter',
      });
    }
    return appDispatch(
      getAnalyseList({
        dataQuery,
        surveyToken,
        ownerToken,
        reportToken,
        from: 0,
        count: 10000,
      })
    );
  },
  requireSeveralUser: (usersList: number[]) =>
    appDispatch(requireSeveralUser({ usersList })),
});

export default withDataContext(
  connect(mapStateToProps, mapDispatchToProps)(RelatedResponsesModal)
);
